// @ts-nocheck — vendored ManualAnnotation camera-math; typechecked upstream, exempt from host strict flags (noUncheckedIndexedAccess/exactOptionalPropertyTypes).
// Parse old EgoCapture21.json (KB4 Kannala-Brandt fisheye) into CalibrationData.
//
// Top-level keys: K, D, R_rel, T_rel, img_sizes
// Camera indices 0=FL, 1=FR (2=L, 3=R when present).
// D is a column vector [[k1],[k2],[k3],[k4]]; T_rel is [[tx],[ty],[tz]];
// img_sizes is [[W,H], ...].

import type { CalibrationData } from '../../types';

type Mat3 = number[][];
type Vec3 = [number, number, number];

export interface RawEgoCapture21 {
  K: number[][][];
  D: number[][][];
  R_rel: number[][][];
  T_rel: number[][][];
  img_sizes: number[][];
}

function mat3Mul(A: Mat3, B: Mat3): Mat3 {
  const C: Mat3 = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) C[i][j] += A[i][k] * B[k][j];
  return C;
}

function mat3Transpose(M: Mat3): Mat3 {
  return [
    [M[0][0], M[1][0], M[2][0]],
    [M[0][1], M[1][1], M[2][1]],
    [M[0][2], M[1][2], M[2][2]],
  ];
}

function mat3MulVec3(M: Mat3, v: Vec3): Vec3 {
  return [
    M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2],
    M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2],
    M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2],
  ];
}

export function parseEgoCapture21(raw: RawEgoCapture21): CalibrationData {
  if (
    !Array.isArray(raw.K) ||
    raw.K.length < 2 ||
    !Array.isArray(raw.D) ||
    raw.D.length < 2 ||
    !Array.isArray(raw.R_rel) ||
    raw.R_rel.length < 2 ||
    !Array.isArray(raw.T_rel) ||
    raw.T_rel.length < 2 ||
    !Array.isArray(raw.img_sizes) ||
    raw.img_sizes.length < 2
  ) {
    throw new Error('EgoCapture21 JSON missing required fields');
  }

  const flatD = (d: number[][]): [number, number, number, number] => [
    d[0][0],
    d[1][0],
    d[2][0],
    d[3][0],
  ];

  const flatT = (t: number[][]): Vec3 => [t[0][0], t[1][0], t[2][0]];

  const base: CalibrationData = {
    cameraModel: 'kb4',
    flK: raw.K[0],
    frK: raw.K[1],
    flD: flatD(raw.D[0]),
    frD: flatD(raw.D[1]),
    R: raw.R_rel[1],
    T: flatT(raw.T_rel[1]),
    flSize: [raw.img_sizes[0][0], raw.img_sizes[0][1]],
    frSize: [raw.img_sizes[1][0], raw.img_sizes[1][1]],
  };

  const hasSideExtrinsics = raw.R_rel.length >= 4 && raw.T_rel.length >= 4;
  const hasSideIntrinsics = raw.K.length >= 4 && raw.D.length >= 4 && raw.img_sizes.length >= 4;
  if (hasSideIntrinsics && !hasSideExtrinsics) {
    console.warn(
      `EgoCapture21 calibration has ${raw.K.length} cameras of intrinsics but only ${raw.R_rel.length} stereo pairs in R_rel/T_rel (need >=4 for L/R epipolar). Side-camera epipolar lines will not be available.`
    );
  }
  if (hasSideIntrinsics && hasSideExtrinsics) {
    const R_fl_l = raw.R_rel[2];
    const T_fl_l = flatT(raw.T_rel[2]);
    const R_fl_r = raw.R_rel[3];
    const T_fl_r = flatT(raw.T_rel[3]);
    const T_fl_fr = flatT(raw.T_rel[1]);
    const R_fr_r = mat3Mul(R_fl_r, mat3Transpose(raw.R_rel[1]));
    const Rt = mat3MulVec3(R_fr_r, T_fl_fr);
    const T_fr_r: Vec3 = [T_fl_r[0] - Rt[0], T_fl_r[1] - Rt[1], T_fl_r[2] - Rt[2]];

    Object.assign(base, {
      lK: raw.K[2],
      lD: flatD(raw.D[2]),
      lSize: [raw.img_sizes[2][0], raw.img_sizes[2][1]] as [number, number],
      R_fl_l,
      T_fl_l,
      rK: raw.K[3],
      rD: flatD(raw.D[3]),
      rSize: [raw.img_sizes[3][0], raw.img_sizes[3][1]] as [number, number],
      R_fr_r,
      T_fr_r,
    });
  }

  return base;
}
